import { Text } from '@fluentui/react-northstar';
import React from 'react';
import { Trans } from 'react-i18next';
import { splitDuration } from './durationFormatter';

/**
 * Renders a minute total with the numbers emphasised but the joining word left in the
 * normal weight - "**22h** and **45m**", matching the design.
 *
 * The emphasis lives inside the translated pattern rather than being applied here, so a
 * translator controls which parts are bold and where they sit in the sentence. That is
 * also why the three duration cases are separate keys: dropping a zero component has to
 * drop its markup with it.
 *
 * Pass `plain` wherever the emphasis is unwanted (the view column, the footer of the
 * ticket detail card). There is no plain-string formatter on this side - the backend's
 * TimeTrackingFormatter produces that for the Teams card.
 */
export const DurationText: React.FC<{ minutes: number; plain?: boolean }> = ({ minutes, plain }) => {
  const split = splitDuration(minutes);

  const key =
    split.hours > 0 && split.minutes > 0
      ? 'duration-hours-minutes'
      : split.hours > 0
        ? 'duration-hours-only'
        : 'duration-minutes-only';

  return (
    <Trans
      i18nKey={`time-tracking.${key}`}
      values={{ hours: split.hours, minutes: split.minutes }}
      // `plain` renders the emphasis slot as ordinary text. Somewhere like a list column
      // the surrounding rows are unemphasised, so bolding the numbers makes the column
      // shout - but the resource strings stay shared rather than duplicated.
      components={{ b: plain ? <span /> : <Text weight="bold" /> }}
    />
  );
};
